import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {ProleisObject} from '@proleis/proleis-rest-client/lib/models/proleis-object-models/proleis.object';
import {
  AnalyticsService,
  CoreService,
  DocumentService,
  Param,
  RequestParam,
  Types,
  UrlService,
} from '@proleis/proleis-rest-client';
import {ProjectStatusModel} from '../modules/orders/project-status/project.status.model';
import {APP_GUIDS, AppGuids} from '../app.guids';
import {FileResponse} from '../models/file.response';
import {saveAs} from 'file-saver';
import {exhaustMap, map, tap} from 'rxjs/operators';
import {NotificationService} from '@proleis/proleis-web-app';
import {PmProjectStatus} from '../modules/orders/project-status/pm/pm-multi-project-status.component';
import {BlankModel} from './models/blank-model';
import {BlankStatusModel} from './models/blank-status-model';

@Injectable({
  providedIn: 'root',
})
export class SuppliersService {
  constructor(
    private httpClient: HttpClient,
    private urlService: UrlService,
    private coreService: CoreService,
    private notificationService: NotificationService,
    @Inject(APP_GUIDS) private guids: AppGuids,
    private documentService: DocumentService,
    private analyticsService: AnalyticsService,
  ) {
  }

  public getSuppliers(): Observable<ProleisObject[] | null> {
    return this.httpClient.get<ProleisObject[] | null>(
      this.urlService.getUrl('supplierapp/purchaseorders/suppliers'),
    );
  }

  public getDeliveryNotes(
    pboId: string,
    pseId: string,
    zqlWhere?: string,
    aliasFields?: string,
  ): Observable<ProleisObject[] | null> {
    let params: HttpParams = new HttpParams();

    if (zqlWhere) {
      params = params.set('zqlWhere', zqlWhere);
    }
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }

    return this.httpClient.get<ProleisObject[] | null>(
      this.urlService.getUrl(
        `supplierapp/purchaseorders/${pboId}/positions/${pseId}/deliverynotes`,
      ),
      {params},
    );
  }

  public getPositions(
    pboId: string,
    pseId: string,
    type?: string,
    zqlWhere?: string,
    aliasFields?: string,
  ): Observable<ProleisObject[] | null> {
    let params: HttpParams = new HttpParams();

    if (zqlWhere) {
      params = params.set('zqlWhere', zqlWhere);
    }

    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }

    if (type) {
      params = params.set('type', type);
    }

    return this.httpClient.get<ProleisObject[] | null>(
      this.urlService.getUrl(
        `supplierapp/purchaseorders/${pboId}/positions`,
      ),
      {params},
    );
  }

  public getPosition(
    pboId: string,
    posId: string,
    aliasFields?: string,
  ): Observable<ProleisObject | null> {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }
    return this.httpClient.get<ProleisObject | null>(
      this.urlService.getUrl(
        `supplierapp/purchaseorders/${pboId}/positions/${posId}`,
      ),
      {params},
    );
  }

  public getProjectStatus(
    pboId: string,
    pseId: string,
  ): Observable<ProjectStatusModel[] | null> {
    return this.httpClient.get<ProjectStatusModel[] | null>(
      this.urlService.getUrl(
        `supplierapp/purchaseorders/${pboId}/positions/${pseId}/projectstatus`,
      ),
    );
  }

  public updateProjectStatus(
    pboId: string,
    pseId: string,
    statusId: string,
    payload: ProjectStatusModel,
  ): Observable<any> {
    if (payload.NUM_MAT_THICKNESS) {
      let thickness = String(payload.NUM_MAT_THICKNESS);
      thickness = thickness.replace(',', '.');
      payload.NUM_MAT_THICKNESS = thickness;
    }

    if (payload.PRODUCTION_PROGRESS) {
      let progress = payload.PRODUCTION_PROGRESS;
      progress = Number(progress);
      payload.PRODUCTION_PROGRESS = progress;
    }
    return this.httpClient.patch(
      this.urlService.getUrl(
        `supplierapp/purchaseorders/${pboId}/positions/${pseId}/projectstatus/${statusId}`,
      ),
      ProjectStatusModel.toParameters(payload),
    );
  }

  public getExportLabel(): Observable<ProleisObject | null> {
    return this.httpClient.get<ProleisObject | null>(
      this.urlService.getUrl('supplierapp/purchaseorders/labelexport'),
    );
  }

  public getDeliveryNote(
    id: string,
    aliasFields?: string,
  ): Observable<ProleisObject | null> {
    let params: HttpParams = new HttpParams();

    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }

    return this.httpClient.get<ProleisObject | null>(
      this.urlService.getUrl(`orders/purchaseorders/deliverynotes/${id}`),
      {params},
    );
  }

  public getEtaDocuments(
    pboId: string,
    pseId: string,
  ): Observable<ProleisObject[] | null> {
    return this.httpClient.get<ProleisObject[] | null>(
      this.urlService.getUrl(
        `supplierapp/purchaseorders/${pboId}/positions/${pseId}/etadocuments`,
      ),
    );
  }

  public setCommissionNr(objId: string, commissionNr: string) {
    const body = {
      COMMISSION_NR: commissionNr,
    };
    return this.httpClient.patch(
      this.urlService.getUrl(
        `supplierapp/purchaseorders/${objId}/commission`,
      ),
      body,
    );
  }

  public getConstructionStatus(): Observable<ProleisObject[]> {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl('supplierapp/purchaseorders/constructionstatus'),
    );
  }

  public getTooltypes(): Observable<ProleisObject[]> {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl('supplierapp/purchaseorders/tooltypes'),
    );
  }

  public getChannels(): Observable<ProleisObject[]> {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl('supplierapp/purchaseorders/channels'),
    );
  }

  public hasOrderChanged(obj: ProleisObject) {
    return obj.EXT_DWS_CHANGE_NOTICE === true;
  }

  public hasPseChangeDocument(pos: ProleisObject) {
    return pos.DWS_CHANGE_DOCU_ID > 0;
  }

  public isOrderDelivered(obj: ProleisObject) {
    return obj.FREIGABE_GUID === this.guids.GUID_ORDER_DELIVERED_STATUS;
  }

  public acceptChange(obj: ProleisObject) {
    this.coreService
      .executeAction(
        obj.OBJECT_ID,
        `§${this.guids.GUID_ACTION_ACCEPT_CHANGE}`,
        null,
      )
      .subscribe((result) => {
        console.log(result);
      });
  }

  public createBmDelivery(
    pbo: ProleisObject,
    pos: ProleisObject,
  ): Observable<any> {
    return this.coreService
      .executeAction<ProleisObject>(
        pos.OBJECT_ID,
        `§${this.guids.GUID_ACTION_CREATE_BM_DELIVERY}`,
        null,
      )
      .pipe(
        exhaustMap((result) => {
          this.notificationService.showNotification('Lieferschein erzeugt');
          return of(pos).pipe(
            tap((updatedPos) => {
              this.documentService
                .getDocumentFile(result.value)
                .subscribe((response) => {
                  const fileResponse: FileResponse =
                    FileResponse.parseResponse(response);
                  saveAs(fileResponse.content, fileResponse.fileName);
                });
            }),
            map((updatedPos) => {
              updatedPos.PBO_DELIVERY_DOC_ID = result.value;
              return updatedPos;
            }),
          );
        }),
      );
  }

  public getPlannedDeliveries(
    pboId: string,
    pseId: string,
    aliasFields?: string,
  ): Observable<ProleisObject[]> {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/planneddeliveries`
      ),
      {params},
    );
  }

  public createPlannedDelivery(
    pboId: string,
    pseId: string,
    pboSubtypeGuid: string,
    plannedDate: Date,
    plannedCount: number,
    deliveryComment?: string,
    deliveryNoteNr?: string,
    incidentId?: string,
  ): Observable<any> {
    const params: Param[] = [];
    params.push({
      name: 'STEP_DELIVERY_DATE',
      type: Types.DATE,
      value: plannedDate.toISOString(),
    });

    params.push({
      name: 'STEP_DELIVERY_COUNT',
      type: Types.DOUBLE,
      value: plannedCount,
    });

    params.push({
      name: 'STEP_PBO_SUBTYP_GUID',
      type: Types.TEXT,
      value: pboSubtypeGuid,
    });

    if (deliveryComment) {
      params.push({
        name: 'STEP_DELIVERY_COMMENT',
        type: Types.TEXT,
        value: deliveryComment,
      });
    }

    if (deliveryNoteNr) {
      params.push({
        name: 'STEP_DELIVERY_NOTE_NUMBER',
        type: Types.TEXT,
        value: deliveryNoteNr,
      });
    }

    if (incidentId) {
      params.push({
        name: 'STEP_VORFALL_ID',
        type: Types.DOUBLE,
        value: incidentId,
      });
    }

    return this.httpClient.post(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/planneddeliveries`,
      ),
      params,
    );
  }

  public updatePlannedDelivery(
    pboId: string,
    pseId: string,
    deliveryId: string,
    plannedCount: number,
    plannedDate: Date,
    deliveryComment: string,
    incidentId?: string,
  ): Observable<any> {
    const body: Param[] = [];
    if (plannedCount) {
      body.push({
        name: 'PLANNED_COUNT',
        value: plannedCount,
        type: Types.DOUBLE,
      });
    }

    if (plannedDate) {
      body.push({
        name: 'PLANNED_DATE',
        value: plannedDate.toISOString(),
        type: Types.DATE,
      });
    }

    body.push({
      name: 'DELIVERYNOTE_COMMENT',
      value: deliveryComment,
      type: Types.TEXT,
    });

    if (incidentId) {
      body.push({
        name: 'LINK_VORFALL_ID',
        value: incidentId,
        type: Types.DOUBLE,
      });
    }

    return this.httpClient.patch(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/planneddeliveries/` +
        deliveryId,
      ),
      body,
    );
  }

  public deletePlannedDelivery(
    pboId: string,
    pseId: string,
    deliveryId: string,
  ): Observable<any> {
    return this.httpClient.delete(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/planneddeliveries/${deliveryId}`,
      ),
    );
  }

  public searchPosition(name: string): Observable<PositionSearchResultModel> {
    let params: HttpParams = new HttpParams();
    if (name) {
      params = params.set('name', name);
    }
    return this.httpClient.get<PositionSearchResultModel>(
      this.urlService.getUrl('/supplierapp/purchaseorders/positions/search'),
      {params},
    );
  }

  public validatePboSubtypes(pboId: string, pseId: string) {
    let params: HttpParams = new HttpParams()
      .set('pboId', pboId)
      .set('pseID', pseId);

    return this.httpClient.get<ValidatePboSubtypesResult>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/pbosubtypes`,
      ),
      {params},
    );
  }

  public getPartsstock(
    pboId: string,
    pseId: string,
    ex: boolean,
  ): Observable<number> {
    let params: HttpParams = new HttpParams().set('ex', ex);
    return this.httpClient.get<number>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/partsstock`,
      ),
      {params},
    );
  }

  public getIncidents(
    pboId: string,
    pseId: string,
    aliasFields?: string,
    zqlWhere?: string,
  ) {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }

    if (zqlWhere) {
      params = params.set('zqlWhere', zqlWhere);
    }

    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/incidents`,
      ),
      {params},
    );
  }

  public getIncident(
    pboId: string,
    pseId: string,
    incidentId: string,
    aliasFields?: string,
  ) {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }

    return this.httpClient.get<ProleisObject>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/incidents/${incidentId}`,
      ),
      {params},
    );
  }

  public getIncidentComments(
    pboId: string,
    pseId: string,
    incidentId: string,
    aliasFields?: string,
  ) {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }

    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/incidents/${incidentId}/comments`,
      ),
      {params},
    );
  }

  public getIncidentDocuments(
    pboId: string,
    pseId: string,
    incidentId: string,
    aliasFields?: string,
  ) {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }

    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pseId}/incidents/${incidentId}/documents`,
      ),
      {params},
    );
  }

  public getPmConstructions(aliasFields?: string): Observable<ProleisObject[]> {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl('/supplierapp/purchaseorders/pm-constructions'),
      {params},
    );
  }

  public getPmConstruction(pseId: string): Observable<ProleisObject> {
    return this.httpClient.get<ProleisObject>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${pseId}`
      ),
    );
  }

  public insertPmIndex(pseId: string, zi: string) {
    const body: Param[] = [{name: 'INDEX_ZI', type: Types.TEXT, value: zi}];

    return this.httpClient.post(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${pseId}/pm-indices`,
      ),
      body,
    );
  }

  public getPmIndices(pseId: string): Observable<ProleisObject[]> {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${pseId}/pm-indices`,
      ),
    );
  }

  public getPmIndex(pseId: string, indexId: string) {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${pseId}/pm-indices/${indexId}`,
      ),
    );
  }

  public insertSnr(
    pseId: string,
    indexId: string,
    snrName: string,
    snrDescription: string,
    indexZi: string,
  ) {
    const body: Param[] = [
      {name: 'INDEX_ZI', type: Types.TEXT, value: indexZi},
      {name: 'SNR_NAME', type: Types.TEXT, value: snrName},
      {name: 'SNR_DESCRIPTION', type: Types.TEXT, value: snrDescription},
    ];

    return this.httpClient.post(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${pseId}/pm-indices/${indexId}/snrs`,
      ),
      body,
    );
  }

  public getPmSnrs(
    pseId: string,
    indexId: string,
  ): Observable<ProleisObject[]> {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${pseId}/pm-indices/${indexId}/snrs`,
      ),
    );
  }

  public getPmSnr(pseId: string, indexId: string, snrId: string) {
    return this.httpClient.get<ProleisObject>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${pseId}/pm-indices/${indexId}/snrs/${snrId}`,
      ),
    );
  }

  public getPartDocuments(
    constructionId: string,
    indexId: string,
    snrId: string,
    aliasFields?: string,
  ): Observable<ProleisObject[]> {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${constructionId}/pm-indices/${indexId}/snrs/${snrId}/documents`,
      ),
      {params},
    );
  }

  public getPrismaDocuments(
    snr: string,
    withAi = false,
  ): Observable<ProleisObject[]> {
    const params: Param[] = [];
    if (snr) {
      params.push({
        name: 'REST_FILTER',
        type: Types.TEXT,
        value: snr,
      });
    }
    if (withAi) {
      params.push({
        name: 'WITH_AI',
        type: Types.BOOL,
        value: withAi,
      });
    }

    return this.analyticsService.executeReport(
      '3aa1be92-2c25-11b2-80d5-2d37df7b1da7',
      params,
    );
  }

  public getPmContactExtPlanners(
    pboId: string,
    pboPosId: string,
  ): Observable<ProleisObject[]> {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pboPosId}/extplanners`,
      ),
    )
      ;
  }

  public getPmContactExtMetrologists(
    pboId: string,
    pboPosId: string,
  ): Observable<ProleisObject[]> {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pboPosId}/extmetrologists`
      ),
    );
  }

  public getPmProjectStatus(
    pboId: string,
    pboPosId: string,
  ): Observable<ProleisObject[]> {
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pboPosId}/pm-projectstatus`,
      ),
    );
  }

  public createPmProjectStatus(pboPosId: string): Observable<any> {
    return this.coreService.executeAction(
      pboPosId,
      '§44fa77fe-2d41-11b2-80d2-514ec8c917e1',
    );
  }

  public patchPmProjectStatus(
    pboId: string,
    pboPosId: string,
    updatedObject: ProleisObject,
  ): Observable<any> {
    const body: RequestParam = {
      DATE_ACCEPTANCE: updatedObject.DATE_ACCEPTANCE,
      DATE_LATEST_RELEASE_CONST: updatedObject.DATE_LATEST_RELEASE_CONST,
      DATE_LATEST_RELEASE_SPEC: updatedObject.DATE_LATEST_RELEASE_SPEC,
      DATE_KICKOFF: updatedObject.DATE_KICKOFF,
    };
    return this.httpClient.patch(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${pboPosId}/pm-projectstatus/${updatedObject.PBO_PM_PROJECT_INFO_ID}`,
      ),
      body,
    );
  }

  public getPmProjectStatusPositionsBulkedit(pboId: string): Observable<any> {
    return this.httpClient.get<any>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/pm-positions-bulkedit`,
      ),
    );
  }

  public getPmIndexComments(
    pseId: string,
    indexId: string,
    aliasFields?: string,
  ): Observable<ProleisObject[]> {
    let params: HttpParams = new HttpParams();
    if (aliasFields) {
      params = params.set('aliasFields', aliasFields);
    }
    return this.httpClient.get<ProleisObject[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/pm-constructions/${pseId}/pm-indices/${indexId}/comments`,
      ),
      {params},
    );
  }

  public bulkEditPmProjectStatus(pboId: string, data: PmProjectStatus[]) {
    return this.httpClient.post(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/pm-positions-bulkedit`,
      ),
      {
        data,
      },
    );
  }

  public updatePmContacts(
    pboId: string,
    posId: string,
    payload: UpdatePmContacts,
  ) {
    return this.httpClient.patch(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${posId}/contacts`,
      ),
      payload,
    );
  }

  public getBlanks(pboId: string, posId: string): Observable<BlankModel[]> {
    return this.httpClient.get<BlankModel[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${posId}/blanks`,
      ),
    );
  }

  public getBlankStatus(
    pboId: string,
    posId: string,
  ): Observable<BlankStatusModel[]> {
    return this.httpClient.get<BlankStatusModel[]>(
      this.urlService.getUrl(
        `/supplierapp/purchaseorders/${pboId}/positions/${posId}/blankstatus`,
      ),
    );
  }

  public createBlankStatus(
    posId: string,
    blankId: string,
    additionalDemand: number,
    damagePreManufacturing: number,
    damageManufacturing: number,
    info: string,
  ) {
    const payload: RequestParam = {
      ADDITIONAL_DEMAND: additionalDemand,
      DAMAGE_PRE_MANUFACTURING: damagePreManufacturing,
      DAMAGE_MANUFACTURING: damageManufacturing,
      INFO: info,
      BLANK_ID: blankId
    };
    return this.coreService.executeAction(
      posId,
      `§${this.guids.GUID_SCRIPT_TEILE_CREATE_PROJECT_INFO_PLATINEN}`,
      payload,
    );
  }

  public deleteBlankStatus(posId: string, blankId: string) {
    const payload: RequestParam = {
      PLA_PROJECT_INFO_ID: blankId
    };
    return this.coreService.executeAction(
      posId,
      `§${this.guids.GUID_SCRIPT_TEILE_DELETE_PROJECT_INFO_PLATINEN}`,
      payload,
    );
  }

  public updateMeasures(id: string, pseId: string, blankId: string, update: UpdateMeasure) {
    return this.httpClient.post(this.urlService.getUrl(
        `/supplierapp/purchaseorders/${id}/positions/${pseId}/blanks/${blankId}/measure`),
      update
    )
  }
}

export interface PositionSearchResult {
  name: string;
  pboId: string;
  posId: string;
  icon: string;
}

export interface PositionSearchResultModel {
  results: PositionSearchResult[];
}

export interface ValidatePboSubtypesResult {
  result: ValidatePboModel[];
}

export interface ValidatePboModel {
  name: string;
  description: string;
  guid: string;
}

export interface UpdatePmContacts {
  extMetrologistId: number;
  extPlannerId: number;
}

export interface UpdateMeasure {
  measureType: 'PLA_MA_AN' | 'PLA_MA_AB',
  length: number;
  width: number;
  amount: number;
}
